// landing/LandingPage.tsx — what a signed-out visitor sees at "/".
//
// 1.24.60: the site's front door. Before this, a stranger landed straight in
// the guest feed with nothing explaining what the platform is or why it is
// different. The pitch is Council-first (GROWTH-PLAN §3.1): transparent,
// accountable moderation is the product; free speech is the value underneath.
//
// 1.24.70: the door dressed for desktop. The old single 76ch column wasted
// two-thirds of a wide screen and ended in a footer that drowned in the
// backdrop's sun. Now: full-width bands with one shared inner measure, a
// two-column hero showing the actual product, the pipeline/verdicts/FAQ
// sections (owner-approved new content), and a footer band with its own
// surface so the page ends on legible ground, tight — no scroll past it.
//
// Signed-in members never see this page — HomeOrLanding renders the feed for
// them — and the guest feed itself moved to /feed, which the hero links to,
// so nothing a guest could do before is lost. Rendered inside ShellLayout so
// a visitor keeps the rail and topbar as a way onward (the /report-illegal
// precedent). ShellLayout suppresses the SignupCtaBar here, and only here: the
// page is itself one large conversion surface, and a second fixed bar under it
// would just cover the footer's own CTA.

import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { usePageTitle } from '@/hooks';
import { LandingShowcase } from './sections/LandingShowcase';
import { CouncilSection } from './sections/CouncilSection';
import { HowItWorksSection } from './sections/HowItWorksSection';
import { VerdictsSection } from './sections/VerdictsSection';
import { FiveRulesSection } from './sections/FiveRulesSection';
import { FaqSection } from './sections/FaqSection';
import { GetTheAppSection } from './sections/GetTheAppSection';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 56px;
  padding-bottom: 8px;
`;

// One shared measure for every band's content. Wide enough to use a desktop,
// narrow enough that body copy keeps a readable line length (sections that
// want tighter prose cap their own paragraphs at 62ch, as before).
const Band = styled.div`
  width: 100%;
  max-width: 1140px;
  margin: 0 auto;
  padding: 0 24px;
`;

// The page's own ground floor: a full-measure surface (not bare backdrop) so
// the last screenful is legible over the Sundown sun in every backdrop mode.
const FooterBand = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
  align-items: center;
  text-align: center;
  padding: 36px 24px;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: ${({ theme }) => theme.color.surface};
`;

const FooterCopy = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.color.textPrimary};
`;

const FooterCta = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 42px;
  padding: 10px 24px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-decoration: none;
  border: 1px solid transparent;
  background: ${({ theme }) => theme.color.accentCyan};
  color: ${({ theme }) => theme.color.onAccent};
  transition: transform 80ms ease;
  &:active { transform: translateY(1px); }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: 2px;
  }
`;

const FooterLinks = styled.nav`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 16px;

  a {
    font-family: ${({ theme }) => theme.font.body};
    font-size: ${({ theme }) => theme.fontSize.xs};
    color: ${({ theme }) => theme.color.textMuted};
    text-decoration: none;
    &:hover { text-decoration: underline; }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.color.focusRing};
      outline-offset: 2px;
    }
  }
`;

export function LandingPage() {
  usePageTitle('Moderation you can see');

  return (
    <Page data-testid="landing-page">
      <LandingShowcase />
      <Band>
        <CouncilSection />
      </Band>
      <Band>
        <HowItWorksSection />
      </Band>
      <Band>
        <VerdictsSection />
      </Band>
      <Band>
        <FiveRulesSection />
      </Band>
      <Band>
        <FaqSection />
      </Band>
      <Band>
        <GetTheAppSection />
      </Band>
      <Band>
        <FooterBand aria-label="Join TruXSocial">
          <FooterCopy>Free to join. Five rules. Everything else is yours to say.</FooterCopy>
          <FooterCta to="/signup">Create your account</FooterCta>
          <FooterLinks aria-label="About TruXSocial">
            <Link to="/policies">The rules</Link>
            <Link to="/support">Support TruXSocial</Link>
            <Link to="/report-illegal">Report unlawful content</Link>
          </FooterLinks>
        </FooterBand>
      </Band>
    </Page>
  );
}
